import type { StartedAssistantCampaignGeneration } from "./assistant-campaign-generation-previews";

export type ChatGeneratedAssetsPollingState = {
  batches: Array<{ pendingTaskCount: number }>;
};

export type ChatGeneratedImagesPollingState = {
  batches: Array<{ status: string; generationStatus: string | null }>;
};

export type ChatGeneratedAssetBatchLike = {
  id: string;
  sourceType?: "standalone" | "campaign";
  name: string;
  status: string;
  createdAt: string;
  sourceProposalId?: string | null;
  previewRegenerationCount?: number | null;
  totalTaskCount: number;
  pendingTaskCount: number;
  failedTaskCount: number;
  generatedAssetCount: number;
};

export type ChatCampaignGenerationReviewActionState = {
  campaignId: string;
  campaignName: string;
  canApprove: boolean;
  canRegeneratePreview: boolean;
  previewRegenerationCount: number;
};

export type ChatGeneratedAssetLike = {
  generatedAssetId: string;
  batchId: string;
  batchSourceType?: "standalone" | "campaign";
  batchName: string;
  scheduledPostId: string | null;
  blockId: string;
  position: number;
  slideIndex: number | null;
  slideId: string | null;
  generationTaskKind?: string;
  createdAt: string;
  post?: {
    draft: {
      imageAssetOverrides?: Array<{
        blockId: string;
        generatedAssetId: string;
      }>;
      carouselSlides?: Array<{
        slideId: string;
        slideIndex: number;
        imageAssetOverrides?: Array<{
          blockId: string;
          generatedAssetId: string;
        }>;
      }>;
    } | null;
  } | null;
};

export type ChatGeneratedAssetWithVariations<
  TAsset extends ChatGeneratedAssetLike = ChatGeneratedAssetLike,
> = TAsset & {
  variationCount: number;
  variations: TAsset[];
};

export type ChatGeneratedAssetBatchGroup<
  TBatch extends ChatGeneratedAssetBatchLike = ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike = ChatGeneratedAssetLike,
> = {
  batch: TBatch;
  assets: Array<ChatGeneratedAssetWithVariations<TAsset>>;
};

export type ChatGeneratedPostAssetSlideGroup<
  TAsset extends ChatGeneratedAssetLike = ChatGeneratedAssetLike,
> = {
  key: string;
  blockId: string;
  slideId: string | null;
  slideIndex: number | null;
  primaryGeneratedAssetId: string;
  variants: TAsset[];
};

export type ChatGeneratedPostAssetGroup<
  TBatch extends ChatGeneratedAssetBatchLike = ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike = ChatGeneratedAssetLike,
> = {
  key: string;
  scheduledPostId: string;
  batches: TBatch[];
  isCarousel: boolean;
  slideGroups: Array<ChatGeneratedPostAssetSlideGroup<TAsset>>;
};

export type ChatGeneratedImageBatchLike = {
  id: string;
  sourceGeneratedAssetId: string | null;
  createdAt: string;
  status?: string;
  generationStatus?: string | null;
  variantCount?: number;
};

export type ChatGeneratedImageLike = {
  generatedAssetId: string;
  batchId: string;
  parentGeneratedAssetId: string | null;
  position: number;
  createdAt: string;
};

export type ChatGeneratedImageBatchThread<
  TBatch extends ChatGeneratedImageBatchLike = ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike = ChatGeneratedImageLike,
> = {
  batch: TBatch;
  images: Array<ChatGeneratedImageThread<TBatch, TImage>>;
};

export type ChatGeneratedImageThread<
  TBatch extends ChatGeneratedImageBatchLike = ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike = ChatGeneratedImageLike,
> = {
  image: TImage;
  latestImage: TImage;
  revisionBatches: Array<ChatGeneratedImageBatchThread<TBatch, TImage>>;
};

export type ChatGeneratedAssetOverlayTarget =
  | {
      kind: "focused-editor";
      owner: { type: "standalone" | "campaign"; id: string };
      postId: string;
    }
  | {
      kind: "asset-inspector";
      assetId: string;
      batchId: string;
    };

export type ChatGeneratedInlineMediaItem<
  TAsset extends ChatGeneratedAssetLike = ChatGeneratedAssetLike,
  TImage extends ChatGeneratedImageLike = ChatGeneratedImageLike,
> =
  | {
      kind: "post_asset";
      key: string;
      createdAt: string;
      asset: TAsset;
      primaryGeneratedAssetId: string;
      variantIndex: number;
      variantCount: number;
    }
  | {
      kind: "image";
      key: string;
      createdAt: string;
      image: TImage;
      isLatest: boolean;
    }
  | {
      kind: "pending";
      key: string;
      createdAt: string;
    };

type SortableChatGeneratedInlineMediaItem<
  TAsset extends ChatGeneratedAssetLike,
  TImage extends ChatGeneratedImageLike,
> = {
  item: ChatGeneratedInlineMediaItem<TAsset, TImage>;
  sortCreatedAt: string;
  sortGroupKey: string;
  sortIndex: number;
};

const MAX_VISIBLE_GENERATED_ASSET_SKELETON_COUNT = 3;

export function shouldPollChatGeneratedAssets(
  data: ChatGeneratedAssetsPollingState | null | undefined,
) {
  return data?.batches.some((batch) => batch.pendingTaskCount > 0) ?? false;
}

export function shouldPollChatGeneratedImages(
  data: ChatGeneratedImagesPollingState | null | undefined,
) {
  return (
    data?.batches.some(
      (batch) =>
        batch.status === "queued" ||
        batch.status === "generating" ||
        batch.generationStatus === "queued" ||
        batch.generationStatus === "processing",
    ) ?? false
  );
}

export function getVisibleGeneratedAssetSkeletonCount(pendingTaskCount: number) {
  return Math.max(0, Math.min(pendingTaskCount, MAX_VISIBLE_GENERATED_ASSET_SKELETON_COUNT));
}

export function getStartedCampaignGenerationSkeletonCount(
  generation: Pick<StartedAssistantCampaignGeneration, "request" | "state">,
) {
  const requestedCount =
    generation.state.generationMode === "all_assets" ? generation.request.items.length : 1;
  return getVisibleGeneratedAssetSkeletonCount(requestedCount);
}

export function getCampaignGenerationReviewActionState(input: {
  batches: ChatGeneratedAssetBatchLike[];
  proposalId: string;
}): ChatCampaignGenerationReviewActionState | null {
  const batch = input.batches.find(
    (candidate) =>
      candidate.sourceType === "campaign" && candidate.sourceProposalId === input.proposalId,
  );
  if (!batch || batch.status !== "pending_review") {
    return null;
  }

  const previewReady = batch.generatedAssetCount > 0 && batch.pendingTaskCount === 0;
  if (!previewReady) {
    return null;
  }

  const previewRegenerationCount = batch.previewRegenerationCount ?? 0;
  return {
    campaignId: batch.id,
    campaignName: batch.name,
    canApprove: true,
    canRegeneratePreview: previewRegenerationCount < 1,
    previewRegenerationCount,
  };
}

export function groupChatGeneratedAssetsByBatch<
  TBatch extends ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike,
>(input: { batches: TBatch[]; assets: TAsset[] }): ChatGeneratedAssetBatchGroup<TBatch, TAsset>[] {
  const assetsByBatchId = new Map<string, TAsset[]>();
  for (const asset of input.assets) {
    const batchAssets = assetsByBatchId.get(asset.batchId) ?? [];
    batchAssets.push(asset);
    assetsByBatchId.set(asset.batchId, batchAssets);
  }

  return input.batches.map((batch) => ({
    batch,
    assets: getVisibleChatGeneratedAssets(assetsByBatchId.get(batch.id) ?? []),
  }));
}

export function groupChatGeneratedPostAssets<
  TBatch extends ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike,
>(input: {
  batches: TBatch[];
  assets: TAsset[];
}): Array<ChatGeneratedPostAssetGroup<TBatch, TAsset>> {
  const batchById = new Map(input.batches.map((batch) => [batch.id, batch] as const));
  const batchOrderById = new Map(input.batches.map((batch, index) => [batch.id, index] as const));
  const assetsByPostId = new Map<string, TAsset[]>();

  for (const asset of input.assets) {
    if (!asset.scheduledPostId || !batchById.has(asset.batchId)) {
      continue;
    }

    const postAssets = assetsByPostId.get(asset.scheduledPostId) ?? [];
    postAssets.push(asset);
    assetsByPostId.set(asset.scheduledPostId, postAssets);
  }

  return Array.from(assetsByPostId.entries())
    .map(([scheduledPostId, postAssets]) => {
      const batches = Array.from(new Set(postAssets.map((asset) => asset.batchId)))
        .map((batchId) => batchById.get(batchId))
        .filter((batch): batch is TBatch => Boolean(batch))
        .toSorted((a, b) => (batchOrderById.get(a.id) ?? 0) - (batchOrderById.get(b.id) ?? 0));

      return {
        key: scheduledPostId,
        scheduledPostId,
        batches,
        isCarousel: postAssets.some((asset) => asset.slideId !== null || asset.slideIndex !== null),
        slideGroups: getChatGeneratedPostAssetSlideGroups(postAssets),
      };
    })
    .toSorted((a, b) => {
      const aBatchOrder = Math.min(...a.batches.map((batch) => batchOrderById.get(batch.id) ?? 0));
      const bBatchOrder = Math.min(...b.batches.map((batch) => batchOrderById.get(batch.id) ?? 0));
      if (aBatchOrder !== bBatchOrder) {
        return aBatchOrder - bBatchOrder;
      }

      return a.scheduledPostId.localeCompare(b.scheduledPostId);
    });
}

export function groupChatGeneratedImageBatchesByThread<
  TBatch extends ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike,
>(input: {
  batches: TBatch[];
  images: TImage[];
}): Array<ChatGeneratedImageBatchThread<TBatch, TImage>> {
  const imagesByBatchId = new Map<string, TImage[]>();
  const imageById = new Map(input.images.map((image) => [image.generatedAssetId, image] as const));
  const childBatchesBySourceAssetId = new Map<string, TBatch[]>();

  for (const image of input.images) {
    const batchImages = imagesByBatchId.get(image.batchId) ?? [];
    batchImages.push(image);
    imagesByBatchId.set(image.batchId, batchImages);
  }

  for (const batch of input.batches) {
    if (!batch.sourceGeneratedAssetId) {
      continue;
    }

    const childBatches = childBatchesBySourceAssetId.get(batch.sourceGeneratedAssetId) ?? [];
    childBatches.push(batch);
    childBatchesBySourceAssetId.set(batch.sourceGeneratedAssetId, childBatches);
  }

  const buildImageThread = (image: TImage): ChatGeneratedImageThread<TBatch, TImage> => {
    const revisionBatches = (childBatchesBySourceAssetId.get(image.generatedAssetId) ?? [])
      .toSorted(compareChatGeneratedImageBatches)
      .map(buildThread);
    return {
      image,
      revisionBatches,
      latestImage: getLatestChatGeneratedImage(image, revisionBatches),
    };
  };

  const buildThread = (batch: TBatch): ChatGeneratedImageBatchThread<TBatch, TImage> => ({
    batch,
    images: (imagesByBatchId.get(batch.id) ?? [])
      .toSorted(compareChatGeneratedImages)
      .map(buildImageThread),
  });

  return input.batches
    .filter(
      (batch) => !batch.sourceGeneratedAssetId || !imageById.has(batch.sourceGeneratedAssetId),
    )
    .toSorted(compareChatGeneratedImageBatches)
    .map(buildThread);
}

export function buildInlineChatGeneratedMediaItems<
  TAssetBatch extends ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike,
  TImageBatch extends ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike,
>(input: {
  postAssetGroups: Array<ChatGeneratedPostAssetGroup<TAssetBatch, TAsset>>;
  imageBatchThreads: Array<ChatGeneratedImageBatchThread<TImageBatch, TImage>>;
  pendingPostBatches: TAssetBatch[];
}): Array<ChatGeneratedInlineMediaItem<TAsset, TImage>> {
  const items: Array<SortableChatGeneratedInlineMediaItem<TAsset, TImage>> = [];

  for (const thread of input.imageBatchThreads) {
    for (const imageThread of thread.images) {
      const images = getImageThreadVersionImages(imageThread);
      for (const image of images) {
        const key = `image:${image.generatedAssetId}`;
        items.push({
          item: {
            kind: "image",
            key,
            createdAt: image.createdAt,
            image,
            isLatest: image.generatedAssetId === imageThread.latestImage.generatedAssetId,
          },
          sortCreatedAt: image.createdAt,
          sortGroupKey: key,
          sortIndex: 0,
        });
      }
    }

    if (isChatGeneratedImageBatchActive(thread.batch)) {
      const skeletonCount = Math.max(0, (thread.batch.variantCount ?? 0) - thread.images.length);
      for (let index = 0; index < skeletonCount; index += 1) {
        const key = `image-pending:${thread.batch.id}:${index}`;
        items.push({
          item: {
            kind: "pending",
            key,
            createdAt: thread.batch.createdAt,
          },
          sortCreatedAt: thread.batch.createdAt,
          sortGroupKey: `image-pending:${thread.batch.id}`,
          sortIndex: index,
        });
      }
    }
  }

  for (const group of input.postAssetGroups) {
    const groupCreatedAt = getLatestPostAssetGroupCreatedAt(group);
    const sortGroupKey = `post-asset:${group.scheduledPostId}`;
    let sortIndex = 0;

    const visibleSlideGroups = getInlinePostAssetSlideGroups(group);

    for (const slideGroup of visibleSlideGroups) {
      const variants = slideGroup.variants
        .map((asset, index) => ({ asset, variantIndex: index + 1 }))
        .toSorted((a, b) => compareChatGeneratedAssetDisplayVariants(a.asset, b.asset));

      for (const { asset, variantIndex } of variants) {
        items.push({
          item: {
            kind: "post_asset",
            key: `post-asset:${asset.generatedAssetId}`,
            createdAt: asset.createdAt,
            asset,
            primaryGeneratedAssetId: slideGroup.primaryGeneratedAssetId,
            variantIndex,
            variantCount: slideGroup.variants.length,
          },
          sortCreatedAt: groupCreatedAt,
          sortGroupKey,
          sortIndex,
        });
        sortIndex += 1;
      }
    }
  }

  for (const batch of input.pendingPostBatches) {
    const skeletonCount = getVisibleGeneratedAssetSkeletonCount(batch.pendingTaskCount);
    for (let index = 0; index < skeletonCount; index += 1) {
      const key = `post-asset-pending:${batch.id}:${index}`;
      items.push({
        item: {
          kind: "pending",
          key,
          createdAt: batch.createdAt,
        },
        sortCreatedAt: batch.createdAt,
        sortGroupKey: `post-asset-pending:${batch.id}`,
        sortIndex: index,
      });
    }
  }

  return items.toSorted(compareSortableInlineMediaItems).map(({ item }) => item);
}

function getInlinePostAssetSlideGroups<
  TBatch extends ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike,
>(group: ChatGeneratedPostAssetGroup<TBatch, TAsset>) {
  if (group.isCarousel || group.slideGroups.length <= 1) {
    return group.slideGroups;
  }

  return group.slideGroups.slice(0, 1);
}

export function getChatGeneratedAssetOverlayTarget(
  asset: Pick<
    ChatGeneratedAssetLike,
    "batchId" | "batchSourceType" | "generatedAssetId" | "scheduledPostId"
  >,
): ChatGeneratedAssetOverlayTarget {
  if (asset.scheduledPostId) {
    return {
      kind: "focused-editor",
      owner: {
        type: asset.batchSourceType === "campaign" ? "campaign" : "standalone",
        id: asset.batchId,
      },
      postId: asset.scheduledPostId,
    };
  }

  return {
    kind: "asset-inspector",
    assetId: asset.generatedAssetId,
    batchId: asset.batchId,
  };
}

function compareChatGeneratedAssets(a: ChatGeneratedAssetLike, b: ChatGeneratedAssetLike) {
  const slideIndexComparison = (a.slideIndex ?? 0) - (b.slideIndex ?? 0);
  if (slideIndexComparison !== 0) {
    return slideIndexComparison;
  }

  const positionComparison = a.position - b.position;
  if (positionComparison !== 0) {
    return positionComparison;
  }

  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function compareChatGeneratedAssetVariants(a: ChatGeneratedAssetLike, b: ChatGeneratedAssetLike) {
  const slideIndexComparison = (a.slideIndex ?? 0) - (b.slideIndex ?? 0);
  if (slideIndexComparison !== 0) {
    return slideIndexComparison;
  }

  const positionComparison = a.position - b.position;
  if (positionComparison !== 0) {
    return positionComparison;
  }

  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

function compareChatGeneratedAssetDisplayVariants(
  a: ChatGeneratedAssetLike,
  b: ChatGeneratedAssetLike,
) {
  const createdAtComparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  if (createdAtComparison !== 0) {
    return createdAtComparison;
  }

  return a.generatedAssetId.localeCompare(b.generatedAssetId);
}

function compareChatGeneratedImageBatches(
  a: ChatGeneratedImageBatchLike,
  b: ChatGeneratedImageBatchLike,
) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function compareSortableInlineMediaItems<
  TAsset extends ChatGeneratedAssetLike,
  TImage extends ChatGeneratedImageLike,
>(
  a: SortableChatGeneratedInlineMediaItem<TAsset, TImage>,
  b: SortableChatGeneratedInlineMediaItem<TAsset, TImage>,
) {
  const createdAtComparison =
    new Date(b.sortCreatedAt).getTime() - new Date(a.sortCreatedAt).getTime();
  if (createdAtComparison !== 0) {
    return createdAtComparison;
  }

  const groupComparison = a.sortGroupKey.localeCompare(b.sortGroupKey);
  if (groupComparison !== 0) {
    return groupComparison;
  }

  const indexComparison = a.sortIndex - b.sortIndex;
  if (indexComparison !== 0) {
    return indexComparison;
  }

  return a.item.key.localeCompare(b.item.key);
}

function compareChatGeneratedImages(a: ChatGeneratedImageLike, b: ChatGeneratedImageLike) {
  const positionComparison = a.position - b.position;
  if (positionComparison !== 0) {
    return positionComparison;
  }

  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function getLatestChatGeneratedImage<
  TBatch extends ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike,
>(image: TImage, revisionBatches: Array<ChatGeneratedImageBatchThread<TBatch, TImage>>) {
  return revisionBatches
    .flatMap((batchThread) => batchThread.images.map((imageThread) => imageThread.latestImage))
    .reduce(
      (latest, candidate) =>
        new Date(candidate.createdAt).getTime() > new Date(latest.createdAt).getTime()
          ? candidate
          : latest,
      image,
    );
}

function getVisibleChatGeneratedAssets<TAsset extends ChatGeneratedAssetLike>(
  assets: TAsset[],
): Array<ChatGeneratedAssetWithVariations<TAsset>> {
  const variationsByTargetKey = new Map<string, TAsset[]>();
  for (const asset of assets) {
    const targetKey = getChatGeneratedAssetTargetKey(asset);
    const targetAssets = variationsByTargetKey.get(targetKey) ?? [];
    targetAssets.push(asset);
    variationsByTargetKey.set(targetKey, targetAssets);
  }

  return Array.from(variationsByTargetKey.values())
    .map((variations) => {
      const sortedVariations = variations.toSorted(compareChatGeneratedAssets);
      const currentGeneratedAssetId = getCurrentDraftGeneratedAssetId(sortedVariations[0] ?? null);
      const visibleAsset =
        sortedVariations.find((asset) => asset.generatedAssetId === currentGeneratedAssetId) ??
        sortedVariations[0];

      if (!visibleAsset) {
        return null;
      }

      return {
        ...visibleAsset,
        variationCount: sortedVariations.length,
        variations: [
          visibleAsset,
          ...sortedVariations.filter(
            (asset) => asset.generatedAssetId !== visibleAsset.generatedAssetId,
          ),
        ],
      };
    })
    .filter((asset): asset is ChatGeneratedAssetWithVariations<TAsset> => Boolean(asset))
    .toSorted(compareChatGeneratedAssets);
}

function getChatGeneratedPostAssetSlideGroups<TAsset extends ChatGeneratedAssetLike>(
  assets: TAsset[],
): Array<ChatGeneratedPostAssetSlideGroup<TAsset>> {
  const variantsByTargetKey = new Map<string, TAsset[]>();
  for (const asset of assets) {
    const targetKey = getChatGeneratedAssetTargetKey(asset);
    const targetAssets = variantsByTargetKey.get(targetKey) ?? [];
    targetAssets.push(asset);
    variantsByTargetKey.set(targetKey, targetAssets);
  }

  return Array.from(variantsByTargetKey.entries())
    .map(([key, variants]) => {
      const sortedVariants = variants.toSorted(compareChatGeneratedAssetVariants);
      const primaryAsset = getPrimaryChatGeneratedAsset(sortedVariants);

      if (!primaryAsset) {
        return null;
      }

      return {
        key,
        blockId: primaryAsset.blockId,
        slideId: primaryAsset.slideId,
        slideIndex: primaryAsset.slideIndex,
        primaryGeneratedAssetId: primaryAsset.generatedAssetId,
        variants: sortedVariants,
      };
    })
    .filter((group): group is ChatGeneratedPostAssetSlideGroup<TAsset> => Boolean(group))
    .toSorted((a, b) => compareChatGeneratedAssets(a.variants[0]!, b.variants[0]!));
}

function getPrimaryChatGeneratedAsset<TAsset extends ChatGeneratedAssetLike>(
  sortedVariants: TAsset[],
) {
  const currentGeneratedAssetId = getCurrentDraftGeneratedAssetId(sortedVariants[0] ?? null);
  return (
    sortedVariants.find((asset) => asset.generatedAssetId === currentGeneratedAssetId) ??
    sortedVariants.find((asset) => asset.generationTaskKind === "social_post") ??
    sortedVariants[0] ??
    null
  );
}

function getLatestPostAssetGroupCreatedAt<
  TBatch extends ChatGeneratedAssetBatchLike,
  TAsset extends ChatGeneratedAssetLike,
>(group: ChatGeneratedPostAssetGroup<TBatch, TAsset>) {
  const fallback = group.batches[0]?.createdAt ?? "";

  return group.slideGroups
    .flatMap((slideGroup) => slideGroup.variants)
    .reduce(
      (latest, asset) =>
        new Date(asset.createdAt).getTime() > new Date(latest).getTime() ? asset.createdAt : latest,
      fallback,
    );
}

function getImageThreadVersionImages<
  TBatch extends ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike,
>(thread: ChatGeneratedImageThread<TBatch, TImage>) {
  const versions = flattenImageThreadImages(thread);
  return [
    thread.latestImage,
    ...versions.filter((image) => image.generatedAssetId !== thread.latestImage.generatedAssetId),
  ];
}

function flattenImageThreadImages<
  TBatch extends ChatGeneratedImageBatchLike,
  TImage extends ChatGeneratedImageLike,
>(thread: ChatGeneratedImageThread<TBatch, TImage>): TImage[] {
  return [
    thread.image,
    ...thread.revisionBatches.flatMap((batchThread) =>
      batchThread.images.flatMap((imageThread) => flattenImageThreadImages(imageThread)),
    ),
  ];
}

function isChatGeneratedImageBatchActive(batch: ChatGeneratedImageBatchLike) {
  return (
    batch.status === "queued" ||
    batch.status === "generating" ||
    batch.generationStatus === "queued" ||
    batch.generationStatus === "processing"
  );
}

function getChatGeneratedAssetTargetKey(asset: ChatGeneratedAssetLike) {
  if (!asset.scheduledPostId) {
    return `asset:${asset.batchId}:${asset.generatedAssetId}`;
  }

  const slideIdentity = asset.slideId ?? asset.slideIndex?.toString() ?? null;

  return [
    "post",
    asset.scheduledPostId,
    slideIdentity ? "carousel" : "root",
    slideIdentity,
    asset.blockId,
  ].join(":");
}

function getCurrentDraftGeneratedAssetId(asset: ChatGeneratedAssetLike | null) {
  if (!asset?.post?.draft) {
    return null;
  }

  const overrides =
    asset.slideId !== null || asset.slideIndex !== null
      ? (asset.post.draft.carouselSlides?.find(
          (slide) => slide.slideId === asset.slideId || slide.slideIndex === asset.slideIndex,
        )?.imageAssetOverrides ?? [])
      : (asset.post.draft.imageAssetOverrides ?? []);

  return overrides.find((override) => override.blockId === asset.blockId)?.generatedAssetId ?? null;
}
